import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import { categories, getCategoryBySlug } from '@/mocks/categories';
import { getVideosByCategory } from '@/mocks/videos';
import { getArticlesByCategory } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = getCategoryBySlug(slug || '');

  if (!category) {
    return (
      <div className="min-h-screen bg-background-50 text-foreground-50">
        <Navbar />
        <div className="flex flex-col items-center justify-center py-32">
          <i className="ri-error-warning-line text-5xl text-gray-600 mb-4"></i>
          <h1 className="text-xl font-bold text-white">分类未找到</h1>
          <Link to="/" className="mt-4 text-amber-400 hover:text-amber-300 transition-colors">返回首页</Link>
        </div>
        <Footer />
      </div>
    );
  }

  const videos = getVideosByCategory(category.id, 6);
  const articles = getArticlesByCategory(category.id);

  const otherCategories = categories.filter(cat => cat.id !== category.id);

  return (
    <>
      <ScrollToTop />
      <div className="min-h-screen bg-background-50 text-foreground-50">
        <Navbar />

        <main>
          <Breadcrumb items={[
            { label: '首页', href: '/' },
            { label: '视频分类', href: '/category/sigua-video' },
            { label: category.name },
          ]} />

          {/* Category Hero */}
          <section className="relative w-full overflow-hidden">
            <div className="absolute inset-0">
              <img
                src={category.featuredImage}
                alt={category.name}
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-background-50"></div>
            </div>
            <div className="relative w-full px-4 md:px-6 lg:px-8 py-20 md:py-28">
              <div className="max-w-3xl">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${category.color}20` }}>
                    <i className={`${category.icon} text-2xl`} style={{ color: category.color }}></i>
                  </div>
                  <h1 className="text-3xl md:text-4xl font-bold text-white">{category.name}</h1>
                </div>
                <p className="text-base text-gray-300 max-w-xl leading-relaxed">{category.description}</p>
                <div className="mt-6 flex flex-wrap gap-2">
                  {category.tags.map((tag, i) => (
                    <span key={i} className="px-3 py-1.5 text-xs rounded-lg bg-white/10 text-gray-200 backdrop-blur-sm">{tag}</span>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Category Switcher */}
          <section className="w-full px-4 md:px-6 lg:px-8 -mt-8 relative z-10">
            <div className="max-w-5xl mx-auto bg-background-100/90 backdrop-blur-md rounded-xl border border-white/5 p-3">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs text-gray-500 px-2 whitespace-nowrap">切换分类：</span>
                {categories.map((cat) => (
                  <Link
                    key={cat.id}
                    to={`/category/${cat.slug}`}
                    onClick={() => window.scrollTo({ top: 0, behavior: 'instant' })}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                      cat.id === category.id
                        ? 'bg-amber-400/15 text-amber-400'
                        : 'text-gray-400 hover:text-white hover:bg-white/5'
                    }`}
                  >
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>
          </section>

          {/* Videos Section */}
          <section className="w-full px-4 md:px-6 lg:px-8 py-12 md:py-16">
            <div className="max-w-7xl mx-auto">
              <h2 className="text-xl md:text-2xl font-bold text-white mb-8">
                <span className="text-amber-400">{category.name}</span> — 热门视频
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {videos.map((video) => (
                  <div key={video.id} className="group bg-background-100 rounded-xl overflow-hidden border border-white/5 hover:border-white/10 transition-all">
                    <div className="aspect-video overflow-hidden relative">
                      <img
                        src={video.coverImage}
                        alt={video.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                      <div className="absolute top-3 left-3">
                        <span className="px-2 py-1 text-xs font-semibold rounded-md bg-amber-500 text-black">{video.rating}</span>
                      </div>
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <div className="w-12 h-12 rounded-full bg-amber-500/90 flex items-center justify-center cursor-pointer">
                          <i className="ri-play-fill text-white text-xl ml-0.5"></i>
                        </div>
                      </div>
                    </div>
                    <div className="p-4">
                      <h3 className="font-semibold text-white text-sm line-clamp-1">{video.title}</h3>
                      <p className="text-xs text-gray-500 mt-1 line-clamp-2">{video.description}</p>
                      <div className="mt-3 flex items-center gap-2 text-xs text-gray-500">
                        <span>{video.year}</span>
                        <span className="text-gray-700">·</span>
                        <span>{video.duration}</span>
                      </div>
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {video.tags.map((tag, i) => (
                          <span key={i} className="px-2 py-0.5 text-xs rounded-md bg-white/5 text-gray-500">{tag}</span>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Articles Section */}
          {articles.length > 0 && (
            <section className="w-full px-4 md:px-6 lg:px-8 py-12 md:py-16 bg-background-100">
              <div className="max-w-7xl mx-auto">
                <h2 className="text-xl md:text-2xl font-bold text-white mb-8">
                  <span className="text-amber-400">{category.name}</span> — 相关文章
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                  {articles.map((article) => (
                    <Link
                      key={article.id}
                      to={`/article/${article.slug}`}
                      onClick={() => window.scrollTo({ top: 0, behavior: 'instant' })}
                      className="group bg-background-50 rounded-xl overflow-hidden border border-white/5 hover:border-white/10 transition-all cursor-pointer"
                    >
                      <div className="aspect-[16/9] overflow-hidden">
                        <img
                          src={article.coverImage}
                          alt={article.title}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                          loading="lazy"
                        />
                      </div>
                      <div className="p-5">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-xs text-gray-600">{article.readTime} 分钟阅读</span>
                        </div>
                        <h3 className="font-semibold text-white group-hover:text-amber-400 transition-colors line-clamp-2 leading-snug">{article.title}</h3>
                        <p className="text-sm text-gray-400 mt-2 line-clamp-2">{article.excerpt}</p>
                        <div className="mt-4 flex items-center justify-between text-xs text-gray-600">
                          <span>{article.publishDate}</span>
                          <span><i className="ri-eye-line mr-1"></i>{article.viewCount.toLocaleString()}</span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </section>
          )}

          {/* Other Categories */}
          <section className="w-full px-4 md:px-6 lg:px-8 py-12 md:py-16">
            <div className="max-w-7xl mx-auto">
              <h2 className="text-xl md:text-2xl font-bold text-white mb-8">
                探索其他<span className="text-amber-400">特色视频网站</span>
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
                {otherCategories.map((cat) => (
                  <Link
                    key={cat.id}
                    to={`/category/${cat.slug}`}
                    onClick={() => window.scrollTo({ top: 0, behavior: 'instant' })}
                    className="bg-background-100 rounded-xl p-5 border border-white/5 hover:border-white/10 transition-all text-center cursor-pointer group"
                  >
                    <div className="w-12 h-12 mx-auto mb-3 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${cat.color}15` }}>
                      <i className={`${cat.icon} text-xl`} style={{ color: cat.color }}></i>
                    </div>
                    <h3 className="font-medium text-white text-sm group-hover:text-amber-400 transition-colors">{cat.name}</h3>
                    <p className="text-xs text-gray-600 mt-1">{cat.videoCount.toLocaleString()} 部视频</p>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}